import React, { useContext, useMemo, useState, useCallback } from 'react';
import styled from 'astroturf/react';
import { CiterightContext } from './CiterightContextProvider';
import { Heading } from 'js/design_system/Heading';
import { Body } from 'js/design_system/Body';
import { Button } from 'js/design_system/Button';
import { TextButton } from 'js/design_system/TextButton';
import Badge from 'js/design_system/Badge';
import { Spinner } from 'js/design_system/Spinner';
import { WorkEvaluationCard } from './components/WorkEvaluationCard';
import { EvaluationVote, WorkData } from './types';
import * as arbitraryEvents from 'js/core_webpack/arbitrary_events';
import { getSourceDisplayName, SourceType } from './utils/sourceNames';
import { getPriorityLabel, getPriorityColor } from './utils/claimPriority';

const ScrollableWrapper = styled.div`
  position: fixed;
  top: var(--spacing-lg-64);
  left: 0;
  right: 0;
  bottom: 0;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
`;

const PageContainer = styled.div`
  padding: 24px;
  max-width: 1600px;
  margin: 0 auto;
  background: var(--background-beige-light);
  min-height: 100vh;
`;

const ClaimSection = styled.div`
  background: var(--background-white, #ffffff);
  border: 1px solid var(--stroke-light);
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 24px;
`;

const ColumnsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-top: 24px;
  align-items: start;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const SourceColumn = styled.div`
  background: var(--background-white, #ffffff);
  border: 1px solid var(--stroke-light);
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
`;

const ColumnHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 2px solid var(--stroke-light);
`;

const StatsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: var(--text-secondary);
`;

interface EvaluationPageProps {
  claimId: string;
}

export const EvaluationPage: React.FC<EvaluationPageProps> = ({ claimId }) => {
  const context = useContext(CiterightContext);
  const [votes, setVotes] = useState<Record<string, EvaluationVote>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [savedSuccessfully, setSavedSuccessfully] = useState(false);

  const claim = useMemo(
    () => context?.report?.claims.find((c) => c.id === claimId),
    [context?.report?.claims, claimId],
  );

  const handleVote = (workId: string, vote: EvaluationVote) => {
    setVotes((prev) => ({
      ...prev,
      [workId]: vote,
    }));
  };

  // Group works by source
  const worksBySource = useMemo(() => {
    if (!claim?.search?.suggested_works) {
      return {
        web_search_citations: [],
        refutation_finder: [],
        search_agent: [],
        agentful_web_search_citations: [],
      };
    }

    const grouped = {
      web_search_citations: [] as WorkData[],
      refutation_finder: [] as WorkData[],
      search_agent: [] as WorkData[],
      agentful_web_search_citations: [] as WorkData[],
    };

    claim.search.suggested_works.forEach((work) => {
      const source = work.source || 'refutation_finder';
      if (source in grouped) {
        grouped[source as keyof typeof grouped].push(work);
      }
    });

    return grouped;
  }, [claim?.search?.suggested_works]);

  // Calculate stats for each source
  const getStats = useCallback(
    (works: WorkData[]) => {
      const totalVoted = works.filter((w) => votes[w.work.id]).length;
      const wouldCite = works.filter(
        (w) => votes[w.work.id] === 'would_cite',
      ).length;
      const wouldNotCite = works.filter(
        (w) => votes[w.work.id] === 'would_not_cite',
      ).length;
      const maybeDepends = works.filter(
        (w) => votes[w.work.id] === 'maybe_depends',
      ).length;
      const unmarked = works.length - totalVoted;

      // Calculate quality score: Would Cite = 100, Maybe = 50, Won't Cite = 0
      let qualityScore: number | null = null;
      if (totalVoted > 0) {
        const totalPoints =
          wouldCite * 100 + maybeDepends * 50 + wouldNotCite * 0;
        qualityScore = Math.round(totalPoints / totalVoted);
      }

      return {
        total: works.length,
        totalVoted,
        wouldCite,
        wouldNotCite,
        maybeDepends,
        unmarked,
        qualityScore,
      };
    },
    [votes],
  );

  const totalVotedPapers = useMemo(() => {
    return Object.keys(votes).length;
  }, [votes]);

  const handleGoToFullReport = () => {
    const params = new URLSearchParams(window.location.search);
    const reportId = params.get('report_id');

    // Keep report_id, remove claim_id
    params.delete('claim_id');
    if (reportId) {
      params.set('report_id', reportId);
    }

    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.location.href = newUrl;
  };

  const handleSaveEvaluation = useCallback(() => {
    const reportId = context?.report?.report_id;
    if (!reportId) return;

    setIsSaving(true);

    const webSearchStats = getStats(worksBySource.web_search_citations);
    const refutationFinderStats = getStats(worksBySource.refutation_finder);
    const searchAgentStats = getStats(worksBySource.search_agent);
    const agentfulWebSearchStats = getStats(
      worksBySource.agentful_web_search_citations,
    );

    const totalEvaluated =
      webSearchStats.totalVoted +
      refutationFinderStats.totalVoted +
      searchAgentStats.totalVoted +
      agentfulWebSearchStats.totalVoted;

    try {
      arbitraryEvents.write('CiterightSourceEvaluation', {
        report_id: reportId,
        claim_id: claimId,
        // Quality scores
        web_search_score: webSearchStats.qualityScore,
        refutation_finder_score: refutationFinderStats.qualityScore,
        search_agent_score: searchAgentStats.qualityScore,
        // Web Search votes
        web_search_would_cite:
          webSearchStats.totalVoted > 0 ? webSearchStats.wouldCite : null,
        web_search_would_not_cite:
          webSearchStats.totalVoted > 0 ? webSearchStats.wouldNotCite : null,
        web_search_maybe:
          webSearchStats.totalVoted > 0 ? webSearchStats.maybeDepends : null,
        web_search_total:
          webSearchStats.totalVoted > 0 ? webSearchStats.total : null,
        // Refutation Finder votes
        refutation_finder_would_cite:
          refutationFinderStats.totalVoted > 0
            ? refutationFinderStats.wouldCite
            : null,
        refutation_finder_would_not_cite:
          refutationFinderStats.totalVoted > 0
            ? refutationFinderStats.wouldNotCite
            : null,
        refutation_finder_maybe:
          refutationFinderStats.totalVoted > 0
            ? refutationFinderStats.maybeDepends
            : null,
        refutation_finder_total:
          refutationFinderStats.totalVoted > 0
            ? refutationFinderStats.total
            : null,
        // Search Agent votes
        search_agent_would_cite:
          searchAgentStats.totalVoted > 0 ? searchAgentStats.wouldCite : null,
        search_agent_would_not_cite:
          searchAgentStats.totalVoted > 0
            ? searchAgentStats.wouldNotCite
            : null,
        search_agent_maybe:
          searchAgentStats.totalVoted > 0
            ? searchAgentStats.maybeDepends
            : null,
        search_agent_total:
          searchAgentStats.totalVoted > 0 ? searchAgentStats.total : null,
        // Agentful Web Search quality score
        agentful_web_search_score: agentfulWebSearchStats.qualityScore,
        // Agentful Web Search votes
        agentful_web_search_would_cite:
          agentfulWebSearchStats.totalVoted > 0
            ? agentfulWebSearchStats.wouldCite
            : null,
        agentful_web_search_would_not_cite:
          agentfulWebSearchStats.totalVoted > 0
            ? agentfulWebSearchStats.wouldNotCite
            : null,
        agentful_web_search_maybe:
          agentfulWebSearchStats.totalVoted > 0
            ? agentfulWebSearchStats.maybeDepends
            : null,
        agentful_web_search_total:
          agentfulWebSearchStats.totalVoted > 0
            ? agentfulWebSearchStats.total
            : null,
        // Total
        total_papers_evaluated: totalEvaluated,
      });

      setSavedSuccessfully(true);
      setTimeout(() => setSavedSuccessfully(false), 3000);
    } catch (error) {
      console.error('Failed to save evaluation:', error);
    } finally {
      setIsSaving(false);
    }
  }, [context?.report?.report_id, claimId, worksBySource, getStats]);

  if (!context?.report) {
    return (
      <ScrollableWrapper>
        <PageContainer>
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spinner size={40} />
            <Body
              size="md"
              style={{ marginTop: 16, color: 'var(--text-secondary)' }}
            >
              Loading report...
            </Body>
          </div>
        </PageContainer>
      </ScrollableWrapper>
    );
  }

  if (!claim) {
    return (
      <ScrollableWrapper>
        <PageContainer>
          <ClaimSection>
            <Body size="md" style={{ color: 'var(--text-secondary)' }}>
              Claim not found.
            </Body>
            <Button
              size="sm"
              onClick={handleGoToFullReport}
              style={{ marginTop: 16 }}
            >
              Back to Report
            </Button>
          </ClaimSection>
        </PageContainer>
      </ScrollableWrapper>
    );
  }

  const getQualityScoreColor = (score: number): string => {
    if (score >= 70) {
      return 'var(--brand-colors-success-500)';
    } else if (score >= 40) {
      return 'var(--premium-gold)';
    } else {
      return 'var(--brand-colors-error-500)';
    }
  };

  const renderSourceColumn = (sourceType: SourceType) => {
    const works = worksBySource[sourceType];
    const stats = getStats(works);

    if (works.length === 0) {
      return null;
    }

    return (
      <SourceColumn key={sourceType}>
        <ColumnHeader>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 8,
            }}
          >
            <Heading size="sm" as="h3" style={{ margin: 0 }}>
              {getSourceDisplayName(sourceType)}
            </Heading>
            <Badge
              text={`${works.length} ${
                works.length === 1 ? 'paper' : 'papers'
              }`}
              backgroundColor="var(--brand-colors-neutral-200)"
              color="var(--text-primary)"
              height="24px"
            />
          </div>
          {stats.qualityScore !== null && (
            <div
              style={{
                backgroundColor: 'var(--brand-colors-neutral-100)',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: 12,
              }}
            >
              <Body
                size="xs"
                style={{
                  color: 'var(--text-secondary)',
                  marginBottom: 4,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontWeight: 600,
                }}
              >
                Quality Score
              </Body>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 4,
                }}
              >
                <Body
                  size="lg"
                  bold
                  style={{
                    color: getQualityScoreColor(stats.qualityScore),
                    fontSize: '28px',
                    lineHeight: 1,
                  }}
                >
                  {stats.qualityScore}
                </Body>
                <Body
                  size="md"
                  style={{
                    color: 'var(--text-secondary)',
                  }}
                >
                  / 100
                </Body>
              </div>
              <Body
                size="xs"
                style={{
                  color: 'var(--text-secondary)',
                  marginTop: 4,
                }}
              >
                {stats.totalVoted} {stats.totalVoted === 1 ? 'paper' : 'papers'}{' '}
                evaluated
              </Body>
            </div>
          )}
          <StatsRow>
            <Badge
              text={`✓ ${stats.wouldCite} Would cite`}
              backgroundColor="var(--brand-colors-success-100)"
              color="var(--brand-colors-success-900)"
              height="22px"
            />
            <Badge
              text={`✗ ${stats.wouldNotCite} Would not cite`}
              backgroundColor="var(--brand-colors-error-100)"
              color="var(--brand-colors-error-500)"
              height="22px"
            />
            <Badge
              text={`? ${stats.maybeDepends} Maybe`}
              backgroundColor="var(--brand-colors-neutral-200)"
              color="var(--brand-colors-neutral-900)"
              height="22px"
            />
            <Badge
              text={`${stats.unmarked} Unmarked`}
              backgroundColor="var(--brand-colors-neutral-100)"
              color="var(--text-secondary)"
              height="22px"
            />
          </StatsRow>
        </ColumnHeader>
        {works.map((work: WorkData, index: number) => (
          <WorkEvaluationCard
            key={work.work.id}
            workData={work}
            vote={votes[work.work.id] || null}
            onVote={(vote) => handleVote(work.work.id, vote)}
            index={index}
          />
        ))}
      </SourceColumn>
    );
  };

  const hasAnyWorks =
    worksBySource.web_search_citations.length > 0 ||
    worksBySource.refutation_finder.length > 0 ||
    worksBySource.search_agent.length > 0 ||
    worksBySource.agentful_web_search_citations.length > 0;

  return (
    <ScrollableWrapper>
      <PageContainer>
        <div
          style={{
            marginBottom: 24,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <TextButton icon="arrow_back" onClick={handleGoToFullReport}>
            Go to Full Report
          </TextButton>
          <Button
            size="sm"
            onClick={handleSaveEvaluation}
            disabled={totalVotedPapers === 0 || isSaving}
            icon={savedSuccessfully ? 'check' : 'save'}
          >
            {savedSuccessfully
              ? 'Saved!'
              : isSaving
                ? 'Saving...'
                : 'Save Evaluation'}
          </Button>
        </div>

        <ClaimSection>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: 16,
            }}
          >
            <Heading size="md" as="h1" style={{ margin: 0 }}>
              Source Evaluation
            </Heading>
            {claim.priority && (
              <Badge
                text={getPriorityLabel(claim.priority, claim.top_claims_count)}
                backgroundColor={
                  getPriorityColor(claim.priority, claim.top_claims_count)
                    .backgroundColor
                }
                color={
                  getPriorityColor(claim.priority, claim.top_claims_count).color
                }
                height="24px"
              />
            )}
          </div>
          <div
            style={{
              backgroundColor: 'var(--background-beige-light)',
              padding: '12px',
              borderLeft: `4px solid ${
                getPriorityColor(claim.priority, claim.top_claims_count)
                  .backgroundColor || 'transparent'
              }`,
              borderRadius: '4px',
            }}
          >
            <Body size="md" style={{ fontStyle: 'italic' }}>
              "{claim.text}"
            </Body>
          </div>
          {claim.reason && (
            <Body
              size="sm"
              style={{ marginTop: 12, color: 'var(--text-secondary)' }}
            >
              {claim.reason}
            </Body>
          )}
        </ClaimSection>

        {!claim.search?.done ? (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <Spinner size={40} />
            <Body
              size="md"
              style={{ marginTop: 16, color: 'var(--text-secondary)' }}
            >
              Searching for citations...
            </Body>
          </div>
        ) : !hasAnyWorks ? (
          <EmptyState>
            <Heading size="sm" as="h3" style={{ marginBottom: 8 }}>
              No Results Found
            </Heading>
            <Body size="md">
              No suggested works were found for this claim. Try refining the
              search query.
            </Body>
          </EmptyState>
        ) : (
          <ColumnsContainer>
            {renderSourceColumn('web_search_citations')}
            {renderSourceColumn('refutation_finder')}
            {renderSourceColumn('search_agent')}
            {renderSourceColumn('agentful_web_search_citations')}
          </ColumnsContainer>
        )}
      </PageContainer>
    </ScrollableWrapper>
  );
};
